import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * `icon` accepts either a component type (e.g. `icon={Wrench}` from
 * lucide-react) or a JSX element (e.g. `icon={<Wrench className="…" />}`).
 * Passing the component type is the common case and avoids a JSX wrapper
 * at every call site.
 */
type IconLike = React.ComponentType<{ className?: string }> | React.ReactElement;

interface EmptyStateProps {
  icon?: IconLike;
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
  /** "card" wraps in a bordered surface; "inline" is unwrapped */
  variant?: "card" | "inline";
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
  variant = "card",
}: EmptyStateProps) {
  // Render the icon: if it's a component, instantiate it; if it's already
  // a JSX element, render as-is.
  let iconNode: React.ReactNode = null;
  if (icon) {
    if (React.isValidElement(icon)) {
      iconNode = icon;
    } else {
      const Icon = icon as React.ComponentType<{ className?: string }>;
      iconNode = <Icon />;
    }
  }

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center gap-3 px-6 py-10",
        variant === "card" && "bg-surface border border-dashed border-border rounded-lg",
        className,
      )}
    >
      {iconNode ? (
        <div className="inline-flex size-12 items-center justify-center rounded-full bg-bg-subtle text-fg-subtle [&_svg]:size-5">
          {iconNode}
        </div>
      ) : null}
      <div className="max-w-sm">
        <h3 className="text-[15px] font-semibold tracking-tight">{title}</h3>
        {description ? (
          <p className="text-sm text-fg-muted mt-1 leading-relaxed">{description}</p>
        ) : null}
      </div>
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}
